import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { SubClient } from '../../types';
import { subClientService } from '../../services/api';

interface SubClientSearchModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (subClient: SubClient) => void;
  onCreateNew: () => void;
}

const SubClientSearchModal: React.FC<SubClientSearchModalProps> = ({
  open,
  onClose,
  onSelect,
  onCreateNew
}) => {
  const [subClients, setSubClients] = useState<SubClient[]>([]);
  const [filteredSubClients, setFilteredSubClients] = useState<SubClient[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchSubClients();
    }
  }, [open]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = subClients.filter(subClient => {
        const query = searchQuery.toLowerCase();
        return (
          subClient.name.toLowerCase().includes(query) ||
          subClient.email.toLowerCase().includes(query) ||
          subClient.phone?.toLowerCase().includes(query) ||
          subClient.company_name?.toLowerCase().includes(query)
        );
      });
      setFilteredSubClients(filtered);
    } else {
      setFilteredSubClients(subClients);
    }
  }, [searchQuery, subClients]);

  const fetchSubClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await subClientService.getAll();
      setSubClients(response.data.data);
      setFilteredSubClients(response.data.data);
    } catch (err: any) {
      setError('Erreur lors du chargement des sous-clients');
      console.error('Error fetching sub-clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (subClient: SubClient) => {
    onSelect(subClient);
    onClose();
  };

  const handleCreateNew = () => {
    onClose();
    onCreateNew();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Rechercher un sous-client</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Rechercher par nom, email, téléphone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            sx={{ mb: 2 }}
          >
            Créer un nouveau sous-client
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {filteredSubClients.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery.trim() 
                    ? 'Aucun sous-client trouvé' 
                    : 'Aucun sous-client disponible'}
                </Typography>
              </Box>
            ) : (
              <List>
                {filteredSubClients.map((subClient) => (
                  <React.Fragment key={subClient.id}>
                    <ListItem
                      disablePadding
                      secondaryAction={
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleSelect(subClient)}
                        >
                          Sélectionner
                        </Button>
                      }
                    >
                      <ListItemButton onClick={() => handleSelect(subClient)}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: subClient.type === 'company' ? 'primary.main' : 'secondary.main' }}>
                            {subClient.type === 'company' ? <BusinessIcon /> : <PersonIcon />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1">
                                {subClient.display_name || subClient.name}
                              </Typography>
                              <Chip
                                label={subClient.type === 'company' ? 'Entreprise' : 'Individuel'}
                                size="small"
                                color={subClient.type === 'company' ? 'primary' : 'secondary'}
                              />
                            </Box>
                          }
                          secondary={
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 14 }} />
                                <Typography variant="caption">{subClient.email}</Typography>
                              </Box>
                              {subClient.phone && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PhoneIcon sx={{ fontSize: 14 }} />
                                  <Typography variant="caption">{subClient.phone}</Typography>
                                </Box>
                              )}
                            </>
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubClientSearchModal;

